<template>
  <v-app id="inspire">
    <v-main :class="{ 'app-main-container': store.state.isUserLoggedIn }">
      <view-mode-tooltip v-if="route.params.projectId && route.params.id && store.state.workflow.isViewOnlyEnabled"/>
      <v-container :fluid="true" style="padding: 0 40px; height: 100%;">
        <template v-if="online">
          <router-view :key="refreshKey"/>
          <snackbar/>
          <v-overlay class="loading-overlay text-center align-center justify-center"
                     :model-value="loadingState || workflowLoadingState || loadingActiveSubscription"
                     persistent>
            <v-progress-circular
              color="white"
              indeterminate
              size="64"
            />
            <div class="loading-message" v-if="loadingMessage">{{ loadingMessage }}</div>
          </v-overlay>
          <v-dialog max-width="500" :modelValue="dialogState">
            <template v-slot:default>
              <v-card title="Warning">
                <v-card-text>
                  {{ dialogMessage }}
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="text"
                    @click="dialogClick('ok')"
                  >Ok
                  </v-btn>
                  <v-btn
                    variant="text"
                    @click="dialogClick('cancel')"
                  >Cancel
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </template>
        <template v-else>
          <div class="d-flex xs6 mt-2">
            <div class="text-h5 workflow-heading">Offline</div>
            <p>
              You appear to be offline! As soon as you are online again you
              will be reconnected.
            </p>up
          </div>
        </template>
      </v-container>
    </v-main>

    <v-footer color="grey-darken-3" :absolute="true" :app="true" class="pb-2 pt-2 app-footer">
      <span class="text-white text-right ml-4 mr-4"
      >&copy; {{ new Date().getFullYear() }} Qlerify</span
      >
      <a
        href="../../Qlerify_terms_of_service_and_privacy_policy.pdf"
        target="_blank"
        class="text-white text-center mr-4"
      >Terms of Service & Privacy Policy</a
      >
      <a
        href="https://www.qlerify.com/cookie-policy"
        target="_blank"
        class="text-white text-center mr-4"
      >Cookie Policy</a
      >
    </v-footer>
    <!-- hidden div for testing web sockets with cypress -->
    <div style="display: none" data-test="websocket" :data-test-version="store.state.wsMessageForCypress?.version"/>
  </v-app>
</template>

<script setup>
import { ref, watch, computed, onMounted, getCurrentInstance, nextTick, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Snackbar from '@/components/Snackbar.vue'
import ViewModeTooltip from '@/components/ViewModeTooltip.vue'
import projectApi from '@/api/projectApi'
import { EventBus } from '@/event-bus/event-bus.js'
import useBroadcastChannel from '@/composables/useBroadcastChannel'
import useCheckAppVersion from '@/composables/useCheckAppVersion'
import { getSubscriptionAccessType } from '@/utils/payments'

const online = ref(true)
const refreshKey = ref(0)

const store = useStore()
const router = useRouter()
const route = useRoute()
const { proxy } = getCurrentInstance()
const { setupBroadcastChannel } = useBroadcastChannel()
useCheckAppVersion()

const loadingState = computed(() => store.getters['loading/getLoadingState'])
const workflowLoadingState = computed(() => store.getters['workflow/getLoadingState'])
const loadingActiveSubscription = computed(() => store.getters['loading/getLoadingActiveSubscription'])
const loadingMessage = computed(() => store.getters['loading/getLoadingMessage'])
const dialogState = computed(() => store.getters['dialog/getDialogState'])
const dialogMessage = computed(() => store.getters['dialog/getDialogMessage'])
const dialogCallback = computed(() => store.getters['dialog/getDialogCallback'])
const userInfo = computed(() => store.state.userInfo)

const dialogClick = (action) => {
  dialogCallback.value(action)
  store.commit('dialog/closeDialog')
}

const setProject = (item) => {
  router.push(`/dashboard/${item.id}`).catch(() => {
  })
}

const updateUserQueries = async (user) => {
  if (user) {
    // run listProjectMembershipsForCurrentUser first
    // because this one must be completed before we can
    // updateTeamMemberLastAccessToProject in the project
    // watcher. This is to avoid exceptions in Cypress
    proxy.$loadingState.start()

    try {
      await store.dispatch('projectMemberships/listProjectMembershipsForCurrentUser')
    } catch (error) {
      store.commit('logout')
      router.push({ path: '/login' })
      return
    }

    const organisationsPromise = store.dispatch('organisations/listOrganisations')
    const getCurrentUserPromise = store.dispatch('user/getCurrentUser')
    await Promise.all([organisationsPromise, getCurrentUserPromise])
    proxy.$loadingState.stop()
  }
}

const updateProjectQueries = async (projectId) => {
  if (projectId) {
    const teamMembersPromise = store.dispatch('teamMembers/listTeamMembers', { projectId })
    const workflowsPromise = store.dispatch('workflows/listWorkflows', projectId)
    await Promise.all([teamMembersPromise, workflowsPromise])
    store.commit('projectMemberships/setActiveProjectId', projectId)
  }
}

onMounted(() => {
  EventBus.$on('set-project', (item) => {
    setProject(item)
  })
  EventBus.$on('redirect-to-login', () => {
    router.push({ path: '/login' })
  })
  EventBus.$on('refresh-dashboard', () => {
    // if the current route is the dashboard, refresh the page
    if (route.path === '/dashboard' || route.path === '/dashboard/') {
      refreshKey.value++
    }
  })

  setupBroadcastChannel((action, payload) => {
    switch (action) {
      case 'LOGOUT':
        if (payload?.loggedInUser === userInfo.value) {
          // If the user is logged in across multiple tabs within the same browser
          window.location.reload()
        }
        break
      default:
        console.warn(`Unknown action: ${action}`)
    }
  })
})

onBeforeUnmount(() => {
  EventBus.$off('set-project')
  EventBus.$off('redirect-to-login')
  EventBus.$off('refresh-dashboard')
})

watch(() => store.state.userId, async (user) => {
  if (user) {
    try {
      proxy.$loadingState.start()
      await updateUserQueries(user)
      proxy.$loadingState.stop()
    } catch (error) {
      console.log('error', error)
      store.commit('logout')
      proxy.$loadingState.stop()
      router.push({ path: '/login' })
    }
  }
}, { immediate: true })

watch(() => store.state.user.user, async (user) => {
  if (user) {
    store.commit('loading/setLoadingActiveSubscription', true)
    await store.dispatch('payments/getPayerUserSubscription')
    const activeSubscription = await store.dispatch('payments/getActiveSubscription')
    const subscriptionAccessType = getSubscriptionAccessType(activeSubscription)

    if (subscriptionAccessType === 'NO_ACCESS') {
      router.push({ path: '/plans' })
    }
    store.commit('loading/setLoadingActiveSubscription', false)
  }
})

watch(() => route.params.projectId, async (projectId) => {
  if (projectId && store.state.isUserLoggedIn) {
    try {
      proxy.$loadingState.start()
      await updateProjectQueries(projectId)
      await nextTick(() => {
        projectApi.updateTeamMemberLastAccessToProject(projectId)
      })
      proxy.$loadingState.stop()
    } catch (error) {
      console.log(error)
      store.commit('logout')
      proxy.$loadingState.stop()
      router.push({ path: '/login' })
    }
  } else {
    store.commit('projectMemberships/setActiveProjectId', null)
  }
}, { immediate: true })
</script>

<style scoped>
#app {
  min-width: 1280px;
}

.v-toolbar__title {
  margin-left: 0;
}

.app-footer {
  z-index: 7 !important;
}

.projects-list-menu {
  max-height: calc(100vh - 20px);
}
</style>

<style>

:root {
  --blue-1: #209AD6;
  --blue-2: #C3DAF6;
  --blue-3: #4285F4;
  --blue-4: #0D66D0;
  --red-1: #BA274A;
  --red-2: #e6899f;
  --background: #FAF7F5;
  --text-light: #4F5359;
  --text-dark: #1F2833;
  --light-1: #FCFCFC;
  --light-2: #F5F2F0;
  --light-3: #E5E3E1;
  --light-4: #CFD3D9;
  --light-5: #C8CCCA;
  --light-6: #D4D9D7;
  --dark-1: #A3ADB9;
  --dark-2: #878F99;
  --dark-3: #61676E;
  --dark-4: #363A40;
  --dark-5: #1C1F1D;
  --card-1: #9C2BF6;
  --card-2: #FFA26E;
  --card-3: #5ECEF8;
  --card-4: #5E5E38;
  --card-4-alt: #EE7878;
  --card-5: #C04DCA;
  --card-6: #FFA26E;
  --card-7: #4957D4;
  --card-8: #64AB43;
  --card-4-alt2: #BF4965;
  --event-orange: #FFC28D;
  --event-stroke-orange: #B2885D;
  --event-yellow: #FFEB90;
  --event-stroke-yellow: #CCBE7B;
  --event-green: #BDEE7A;
  --event-stroke-green: #88AB78;
  --event-turquoise: #B7EEE4;
  --event-stroke-turquoise: #82BAB0;
  --event-light-blue: #A7EEE1;
  --event-stroke-light-blue: #709FBA;
  --event-purple: #C1C0F4;
  --event-stroke-light-blue-alt: #8786C;
  --event-pink: #ECC0F4;
  --event-stroke-pink: #B986C2;

  --color-primary: #022a44;
  --light-blue: #3183fb;
  --white: #ffffff;
  --plan-card-basic: #3BB0E6;
  --plan-card-pro: #A0F090;
  --plan-card-enterprise: #A091FF;

  font-family: Inter, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18.2px;
  letter-spacing: -0.42px !important;
}

@font-face {
  font-family: 'Faktum';
  src: url('./assets/fonts/Faktum-Bold.otf') format('opentype');
  font-weight: bold;
  font-style: normal;
}

@font-face {
  font-family: 'Faktum';
  src: url('./assets/fonts/Faktum-Regular.otf') format('opentype');
  font-weight: normal;
  font-style: normal;
}

.text-h2 {
  font-family: 'Inter', sans-serif !important;
}

.text-h4 {
  font-family: 'Inter', sans-serif !important;
}

.text-h5 {
  font-family: 'Inter', sans-serif !important;
}

.text-h6 {
  font-family: 'Inter', sans-serif !important;
}

.text-body-1 {
  font-family: 'Inter', sans-serif !important;
}

.text-caption {
  font-family: 'Inter', sans-serif !important;
}

.v-application {
  background-color: var(--background) !important;
}

.v-field {
  font-size: 14px !important;
}

.v-btn:not(.v-btn--icon),
.v-dialog,
.v-tooltip__content,
.v-menu__content,
.v-card:not(.v-card--flat),
.v-expansion-panels {
  border-radius: 10px !important;
}

.v-card-text {
  font-size: 1rem !important;
}

.v-expansion-panel:first-child {
  border-radius: 10px 10px 0 0;
}

.v-expansion-panel:last-child {
  border-radius: 0 0 10px 10px;
}

.v-toolbar {
  background-color: var(--light-1, #FCFCFC) !important;
  color: var(--text-dark) !important;
}

.v-card-actions {
  gap: 12px;
  margin: 0;
  padding: 16px !important;
}

.v-dialog > .v-overlay__content > .v-card > .v-card-text {
  letter-spacing: -0.42px !important;
}

/* Override always uppercase */
.v-btn {
  text-transform: none !important;
  letter-spacing: -0.42px !important;
}

/* Custom styling for v-switch */
.v-switch__track {
  opacity: 0.24 !important;
  background-color: #A3ACB9 !important;
}

.v-switch__thumb {
  background-color: #868E99 !important;
}

.v-selection-control__input::before {
  display: none;
}

.v-selection-control__input:hover > .v-switch__thumb {
  background-color: #6a727c !important;
}

/* Buttons used at bottom of containers */
div.btn-container-bottom {
  display: inline-block;
  padding: 3px 8px;
  margin-right: 4px;
  border-radius: 0 0 4px 4px;
  background: #FCFCFC;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  color: var(--text-light);
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  &.disabled {
    pointer-events: none;
  }

  &:hover {
    background-color: var(--light-2);
  }

  &:active {
    background-color: var(--light-2);
  }

  & > .v-icon {
    width: 20px;
    height: 20px;
  }
}

/* Custom styling for default/small size of variant default (elevated) buttons */
.v-btn.v-btn--icon.v-btn--size-small.v-btn--variant-elevated {
  width: 24px;
}

.v-btn.v-btn--size-small.v-btn--variant-elevated {
  height: 24px;
}

.v-btn.v-btn--icon.v-btn--size-default.v-btn--variant-elevated {
  width: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated {
  height: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated,
.v-btn.v-btn--size-small.v-btn--variant-elevated {
  background-color: var(--light-1);
  border-radius: 3px !important;
  opacity: 1 !important;
  color: var(--text-dark);
  padding: 0 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  border-radius: 4px;
  background: #FCFCFC;
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.42px;
  transition: background-color ease 0.2s;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated:hover,
.v-btn.v-btn--size-small.v-btn--variant-elevated:hover {
  opacity: 1 !important;
  background-color: var(--light-2);
}

.v-btn.v-btn--size-default.v-btn--variant-elevated:disabled,
.v-btn.v-btn--size-small.v-btn--variant-elevated:disabled {
  opacity: 0.3 !important;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated:active,
.v-btn.v-btn--size-small.v-btn--variant-elevated:active {
  opacity: 1 !important;
  background-color: var(--light-3);
}

/* Custom styling for large/default/small size of variant flat buttons */
.v-btn.v-btn--variant-flat {
  background-color: #363A40 !important;
  color: #FCFCFC !important;
}

.v-btn.v-btn--size-large.v-btn--variant-flat {
  height: 42px !important;
  padding: 6px 12px !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
  font-size: 16px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-flat {
  padding: 2px 20px;
  border-radius: 5px;
  color: #FCFCFC;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
}

.v-btn.v-btn--icon.v-btn--size-x-small.v-btn--variant-flat {
  width: 20px !important;
  max-width: 20px !important;
  min-width: 20px !important;
}

.v-btn.v-btn--size-x-small.v-btn--variant-flat {
  height: 20px !important;
  padding: 2px 2px;
  border-radius: 5px;
  color: #FCFCFC;
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
}

.v-btn.v-btn--variant-flat:hover {
  background-color: #61676E !important;
}

.v-btn.v-btn--variant-flat:active {
  background-color: #8e959c !important; /* Not part of spec but makes sense */
}

/* TODO: add default size */

/* Custom styling for large size of variant outlined buttons */
.v-btn.v-btn--size-large.v-btn--variant-outlined {
  border-radius: 8px !important;
  padding: 11px 12px !important;
  height: 42px !important;
  font-weight: 500 !important;
  font-size: 16px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-outlined {
  border-radius: 8px !important;
  border: 1px solid #878F99 !important;
  padding: 5px 8px 5px 12px !important;
  height: 30px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
}

/* Custom styling for small/default plain icon buttons */
.v-btn.v-btn--icon.v-btn--size-small.v-btn--variant-plain {
  width: 16px !important;
  height: 16px !important;
  padding: 0 !important;
  font-size: 10.65px !important;
}

.v-btn.v-btn--icon.v-btn--size-default.v-btn--variant-plain {
  width: 24px !important;
  height: 24px !important;
  font-size: 13.5px !important;
}

/* .v-btn.v-btn--icon.v-btn--size-small.v-btn--variant-plain > .v-btn__content {
  width: 16px !important;
  height: 16px !important;
} */

/* Custom styling for round icon buttons */
.v-btn.v-btn--icon.v-btn--variant-text {
  width: 48px !important;
}

.v-btn.v-btn--variant-text {
  height: 48px !important;
  font-size: 16px !important;
  color: var(--text-light) !important;
}

.v-btn.v-btn--size-small.v-btn--icon.v-btn--variant-text {
  width: 40px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text {
  height: 40px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text {
  font-size: 16px !important;
}

.v-btn.v-btn--icon.v-btn--size-x-small.v-btn--variant-text {
  width: 32px !important;
  max-width: 32px !important;
  min-width: 32px !important;
}

.v-btn.v-btn--size-x-small.v-btn--variant-text {
  height: 32px !important;
  font-size: 13.5px !important;
}

.v-btn.v-btn--variant-text:hover {
  background-color: var(--light-2) !important;
}

.v-btn.v-btn--variant-text:active {
  background-color: var(--light-3) !important;
}

.v-btn.v-btn--active.v-btn--variant-text {
  background-color: var(--light-3) !important;
}

.v-btn.top-nav.v-btn--variant-text:active {
  background-color: var(--blue-2) !important;
}

.v-btn.top-nav.v-btn--active.v-btn--variant-text {
  background-color: var(--blue-2) !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text:active {
  background-color: var(--dark-1) !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text:active {
  background-color: var(--dark-1) !important;
}

/* Custom styling for default/ large tonal buttons (with support for icon and text) */
.v-btn.v-btn--size-default.v-btn--variant-tonal > .v-btn__underlay {
  display: none
}

.v-btn.v-btn--size-large.v-btn--variant-tonal > .v-btn__underlay {
  display: none
}

.v-btn.v-btn--size-default.v-btn--variant-tonal > .v-btn__content > .v-icon {
  line-height: 18px;
}

.v-btn.v-btn--icon.v-btn--size-default.v-btn--variant-tonal {
  width: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-tonal {
  height: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-tonal {
  height: 32px;
  background-color: var(--light-1);
  border-radius: 3px !important;
  opacity: 1 !important;
  color: var(--text-dark);
  padding: 0 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18.2px;
  letter-spacing: -0.42px;
  transition: background-color ease 0.2s;
}

.v-btn.v-btn--size-large.v-btn--variant-tonal {
  padding: 6px 12px !important;
  height: 42px !important;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px !important;
  background-color: var(--light-1);
}

.v-btn.v-btn--size-default.v-btn--variant-tonal:hover {
  opacity: 1 !important;
  background-color: var(--light-2);
}

.v-btn.v-btn--size-default.v-btn--variant-tonal:disabled {
  opacity: 0.3 !important;
}

.v-btn.v-btn--variant-tonal:active {
  opacity: 1 !important;
  background-color: var(--light-3);
}

/* Custom styling for v-text-field with variant solo-filled */
.v-field {
  text-transform: none !important;
}

.v-field.v-field--variant-solo-filled {
  background-color: transparent !important;
  box-shadow: none;
  border-radius: 8px;
  border: 1px solid #CFD3D9;
}

.v-field.v-field--active.v-field--variant-solo-filled {
  border-radius: 10px !important;
  border: 1px solid #868E99;
}

/* No more ripples */
.v-ripple__container {
  display: none;
}

/* Change default background of cards with variant elevated */
.v-card--variant-elevated {
  background-color: var(--light-1) !important;
}

/* Custom styling for v-field with variant solo */
.v-field.v-field--variant-solo {
  background-color: var(--light-1) !important;
}

/* Custom styling for v-list */
.v-list {
  background-color: var(--light-1, #FCFCFC) !important;
}

.v-list-item {
  background-color: var(--light-1, #FCFCFC) !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  transition: background-color 0.15s ease-in-out !important;
}

/* Custom styling for v-list inside v-menu overlay */
.v-menu > .v-overlay__content > .v-list {
  padding: 8px !important;
  border-radius: 10px !important;
  background-color: var(--white, #FFF) !important;
  box-shadow: 8px 4px 24px 0px rgba(0, 0, 0, 0.24) !important;
}

.v-menu > .v-overlay__content > .v-list > .v-list-item {
  background-color: var(--white, #FFF) !important;

  &.releaseFilter, &.sort-item {
    .v-list-item__overlay {
      opacity: 0 !important;
    }

    &.active {
      background-color: var(--light-3, #E5E3E1) !important;
    }
  }
}

.v-list-item__prepend > .v-icon {
  opacity: 1 !important;
}

.v-list-item__prepend > .v-icon ~ .v-list-item__spacer {
  width: 8px !important;
}

.v-list-item:hover > .v-list-item__overlay {
  opacity: 0 !important;
}

.v-list-item:hover {
  background-color: var(--light-2) !important;
}

.v-list-item:active {
  background-color: var(--light-3) !important;
}

/* Custom styling for v-tab */
.new-tab {
  height: 34px !important;
  border-radius: 8px;
  background-color: var(--light-2);
}

.new-tab.large {
  height: 38px !important;
}

.v-slide-group__content {
  justify-content: space-between;
}

.v-btn.v-tab {
  width: auto !important;
  height: 34px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  background-color: transparent !important;
}

.new-tab.large .v-btn.v-tab {
  height: 38px !important;
}

.v-btn.v-tab > .v-btn__content {
  color: var(--text-light);
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
}

.v-btn.v-tab.v-tab--selected > .v-btn__content {
  color: #FCFCFC;
  font-weight: 500;
}

.v-tab__slider {
  z-index: -1;
  background-color: #363A40;
  color: var(--text-light);
  height: 34px !important;
  border-radius: 8px !important;
}

.new-tab.large .v-tab__slider {
  height: 38px !important;
}

.v-btn.v-tab.v-tab--selected:hover {
  color: #FCFCFC !important;
  background-color: transparent !important;
}

.text-title {
  color: var(--text-dark);
  font-size: 20px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.6px;
}

.text-subtitle {
  color: var(--text-dark);
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.6px;
}

.round-bottom,
.round-bottom .v-table,
.round-bottom .v-data-table {
  border-radius: 0 0 10px 10px !important;
}

.v-table {
  background: transparent !important;
}

th {
  height: 36px !important;
}

th > .v-data-table-header__content {
  color: var(--text-dark);
  font-size: 12px;
  font-weight: 600;
  line-height: 100%; /* 12px */
  letter-spacing: -0.36px;
}

td {
  height: 48px;
  color: #4F5359;
  font-size: 16px;
  line-height: 120%; /* 19.2px */
  letter-spacing: -0.48px;
}

.v-tabs-bar {
  border-radius: 10px 10px 0 0;
}

.loading-overlay {
  z-index: 20001 !important;
}

.loading-message {
  color: #fff;
  font-size: 15px;
  margin-top: 20px;
}

.show-on-hover {
  transition: opacity 0.15s ease-in-out !important;
  opacity: 0 !important;
}

.show-on-hover:hover {
  opacity: 1 !important;
}

.hover-container:hover + .show-on-hover {
  opacity: 1 !important;
}

.hover-container:hover > div .show-on-hover {
  opacity: 1 !important;
}

.hover-container:hover > .show-on-hover {
  opacity: 1 !important;
}

.dialog-top-right {
  top: 0;
  right: 0;
  margin: 0 !important;
}

.card {
  border-radius: 12px;
  background: #FCFCFC;
  border: 1px solid #FCFCFC;
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15) !important;
}

.opacity-100 {
  opacity: 1 !important;
}

.opacity-0 {
  opacity: 0 !important;
}

.fit-content {
  width: fit-content !important;
}

.checkbox-container {
  width: 16px;
  height: 16px;
  cursor: pointer;
  align-items: center;
  border-radius: 2px;
  display: inline-flex;
  justify-content: center;
  border: 1px solid var(--text-light, #4F5359);

  &:not(.checked) {
    > * {
      display: none;
    }
  }

  &.checked {
    color: var(--light-1, #FCFCFC);
    border-color: var(--text-light, #4F5359);
    background-color: var(--text-light, #4F5359);
  }
}

.auth-card-wrapper {
  padding-top: 133px;
  padding-bottom: 124px;
}

.auth-card {
  width: 444px;
  margin: 0 auto;
  border-radius: 12px;
  background: #FCFCFC;
  padding: 12px 16px 16px;
  border: 1px solid #FCFCFC;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);

  .auth-card-container {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .auth-card-footer {
    display: flex;
    align-items: center;

    .auth-card-footer-part {
      gap: 10px;
      display: flex;
      align-items: center;

      &.auth-card-footer-part_column {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
}

.height-transition-hover-container {
  .height-transition-container {
    height: 0;
    transition: all 0.2s;

    .show-on-hover-item {
      opacity: 0;
    }
  }

  &:hover {
    .height-transition-container {
      height: var(--transitionContainerHeight, 20px);

      .show-on-hover-item {
        opacity: 1;

        &.v-btn--variant-plain {
          opacity: 0.7;

          &:hover {
            opacity: 1;
          }
        }

        &.v-btn--disabled {
          opacity: 0.26;
        }
      }
    }
  }
}

.v-btn.bg-deep-purple-accent-4 {
  background-color: var(--card-7) !important;
}
</style>
